import { driver, DriveStep, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTranslation } from 'react-i18next';
import { useCallback, useRef } from 'react';
import helpInstructionsGif from 'src/images/tours/help-instructions.gif';
import sporttiaLogo from 'src/images/sporttia-icon.svg';
import { sporttiaColors } from 'src/theme/theme';

// MUI sm breakpoint is 600px
const MOBILE_BREAKPOINT = 600;
// Time to wait for menu animation to complete
const MENU_ANIMATION_DELAY = 350;
// LocalStorage key to track if tour was completed
const TOUR_COMPLETED_KEY = 'sporttia_menus_tour_completed';

/**
 * Hook to create a guided tour for the header menus
 * Uses Driver.js to highlight elements and show explanations
 * Supports both desktop and mobile views
 */
export function useMenusTour() {
    const { t } = useTranslation();
    const driverRef = useRef<Driver | null>(null);

    const isTourCompleted = useCallback(() => {
        return localStorage.getItem(TOUR_COMPLETED_KEY) === 'true';
    }, []);

    const markTourCompleted = useCallback(() => {
        localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
    }, []);

    const isMobile = useCallback(() => {
        return window.innerWidth < MOBILE_BREAKPOINT;
    }, []);

    // En móvil el menú es el sidebar temporal, que marca su estado en
    // `#header[data-mobile-open]` (ver AppSidebar).
    const isMenuOpen = useCallback(() => {
        return (
            document.querySelector('#header[data-mobile-open="true"]') !== null
        );
    }, []);

    const openMobileMenu = useCallback((): Promise<void> => {
        return new Promise((resolve) => {
            if (isMenuOpen()) {
                resolve();
                return;
            }
            const menuButton = document.getElementById('mobile-menu-button');
            if (menuButton) {
                menuButton.click();
                // Wait for menu animation to complete
                setTimeout(resolve, MENU_ANIMATION_DELAY);
            } else {
                resolve();
            }
        });
    }, [isMenuOpen]);

    const closeMobileMenu = useCallback((): Promise<void> => {
        return new Promise((resolve) => {
            if (!isMenuOpen()) {
                resolve();
                return;
            }
            const backdrop = document.querySelector('.MuiBackdrop-root');
            if (backdrop) {
                (backdrop as HTMLElement).click();
                setTimeout(resolve, MENU_ANIMATION_DELAY);
            } else {
                resolve();
            }
        });
    }, [isMenuOpen]);

    const startTour = useCallback(() => {
        const mobile = isMobile();

        // Desktop steps - Step 0 is welcome (no element), steps 1-6 highlight header items
        const desktopSteps: DriveStep[] = [
            {
                popover: {
                    title: ' ',
                    description: `<div style='display: flex; flex-direction: column; align-items: center; text-align: center;'><img src='${sporttiaLogo}' alt='Sporttia' style='width: 120px; margin-bottom: 12px;' /><h2 style='color: ${sporttiaColors.brandText}; margin: 0 0 12px 0; font-size: 1.4rem; font-weight: 600;'>${t('tour.menus.welcome.title')}</h2><p style='margin: 0; color: #444; line-height: 1.5;'>${t('tour.menus.welcome.description')}</p></div>`,
                },
            },
            {
                element: '#header #timetable',
                popover: {
                    title: t('tour.header.timetable.title'),
                    description: t('tour.header.timetable.description'),
                },
            },
            {
                element: '#header #tour-menu-users',
                popover: {
                    title: t('tour.header.users.title'),
                    description: t('tour.header.users.description'),
                },
            },
            {
                element: '#header #messages',
                popover: {
                    title: t('tour.header.messages.title'),
                    description: t('tour.header.messages.description'),
                },
            },
            {
                element: '#header #notifications',
                popover: {
                    title: t('tour.header.notifications.title'),
                    description: t('tour.header.notifications.description'),
                },
            },
            {
                element: '#header #account',
                popover: {
                    title: t('tour.header.account.title'),
                    description: t('tour.header.account.description'),
                },
            },
            {
                element: '#header #account',
                popover: {
                    title: t('tour.header.help.title'),
                    description: `<div class='gif-popover'><p>${t('tour.header.help.description')}</p><img style='padding-top:10px; max-width: 100%' src='${helpInstructionsGif}' /></div>
                    `,
                },
            },
        ];

        // En móvil todos los elementos del tour viven dentro del sidebar, así
        // que los pasos 2..7 necesitan el sidebar abierto. Sólo los pasos 0
        // (bienvenida) y 1 (botón de menú) lo necesitan cerrado.
        const mobileMenuSteps = [2, 3, 4, 5, 6, 7];
        const mobileCloseMenuSteps: number[] = [];

        // Mobile steps
        const mobileSteps: DriveStep[] = [
            {
                // Step 0 - Welcome
                popover: {
                    title: ' ',
                    description: `<div style='display: flex; flex-direction: column; align-items: center; text-align: center;'><img src='${sporttiaLogo}' alt='Sporttia' style='width: 120px; margin-bottom: 12px;' /><h2 style='color: ${sporttiaColors.brandText}; margin: 0 0 12px 0; font-size: 1.4rem; font-weight: 600;'>${t('tour.menus.welcome.title')}</h2><p style='margin: 0; color: #444; line-height: 1.5;'>${t('tour.menus.welcome.description')}</p></div>`,
                },
            },
            {
                // Step 1 - Mobile menu button
                element: '#mobile-menu-button',
                popover: {
                    title: t('tour.mobile.menu.title'),
                    description: t('tour.mobile.menu.description'),
                },
            },
            {
                // Step 2
                element: '#header #timetable',
                popover: {
                    title: t('tour.header.timetable.title'),
                    description: t('tour.header.timetable.description'),
                },
            },
            {
                // Step 3
                element: '#header #tour-menu-users',
                popover: {
                    title: t('tour.header.users.title'),
                    description: t('tour.header.users.description'),
                },
            },
            {
                // Step 4
                element: '#header #messages',
                popover: {
                    title: t('tour.header.messages.title'),
                    description: t('tour.header.messages.description'),
                },
            },
            {
                // Step 5
                element: '#header #notifications',
                popover: {
                    title: t('tour.header.notifications.title'),
                    description: t('tour.header.notifications.description'),
                },
            },
            {
                // Step 6
                element: '#header #account',
                popover: {
                    title: t('tour.header.account.title'),
                    description: t('tour.header.account.description'),
                },
            },
            {
                // Step 7
                element: '#header #account',
                popover: {
                    title: t('tour.header.help.title'),
                    description: `<div class='gif-popover'><p>${t('tour.header.help.description')}</p><img style='padding-top:10px; max-width: 100%' src='${helpInstructionsGif}' /></div>`,
                },
            },
        ];

        const driverObj = driver({
            showProgress: true,
            animate: true,
            allowClose: true,
            stagePadding: 4,
            nextBtnText: t('tour.next'),
            prevBtnText: t('tour.back'),
            doneBtnText: t('tour.last'),
            progressText: '{{current}} / {{total}}',
            steps: mobile ? mobileSteps : desktopSteps,
            onNextClick: () => {
                if (!mobile) {
                    driverObj.moveNext();
                    return;
                }

                const currentStep = driverObj.getActiveIndex() ?? 0;
                const nextStep = currentStep + 1;

                // Check if next step needs menu open
                if (mobileMenuSteps.includes(nextStep)) {
                    openMobileMenu().then(() => {
                        driverObj.moveNext();
                    });
                }
                // Check if next step needs menu closed
                else if (mobileCloseMenuSteps.includes(nextStep)) {
                    closeMobileMenu().then(() => {
                        driverObj.moveNext();
                    });
                } else {
                    driverObj.moveNext();
                }
            },
            onPrevClick: () => {
                if (!mobile) {
                    driverObj.movePrevious();
                    return;
                }

                const currentStep = driverObj.getActiveIndex() ?? 0;
                const prevStep = currentStep - 1;

                // Check if previous step needs menu open
                if (mobileMenuSteps.includes(prevStep)) {
                    openMobileMenu().then(() => {
                        driverObj.movePrevious();
                    });
                }
                // Check if previous step needs menu closed
                else if (mobileCloseMenuSteps.includes(prevStep)) {
                    closeMobileMenu().then(() => {
                        driverObj.movePrevious();
                    });
                }
                // Step 0 (welcome) or Step 1 (menu button) needs menu closed
                else if (prevStep <= 1) {
                    closeMobileMenu().then(() => {
                        driverObj.movePrevious();
                    });
                } else {
                    driverObj.movePrevious();
                }
            },
            onDestroyed: () => {
                // Mark tour as completed so it won't show again
                markTourCompleted();
                // Clean up - close mobile menu if open
                if (mobile) {
                    closeMobileMenu();
                }
            },
        });

        driverRef.current = driverObj;
        driverObj.drive();
    }, [t, isMobile, openMobileMenu, closeMobileMenu, markTourCompleted]);

    return { startTour, isTourCompleted };
}
